import { useCreateRequestMutation } from '@entities/request/api/request-api';
import { useEffect } from 'react';
import toast from 'react-hot-toast';
import { Toast } from '@shared/ui/toast/toast';
import { getQueryError } from '@shared/lib/utils/get-query-error/get-query-error';
import { useCurrentUserSelector } from '@entities/current-user/model/selectors';
import { ProductListItem } from '@shared/model/product';
import { CACHE_KEY_EMPLOYEE_CREATE_REQUEST } from '@entities/request/config/api-constants';
import { useRequestsListSharedQuery } from '@entities/request/libs/hooks/use-request-list-shared-query/use-request-list-shared-query';
import { DEFAULT_REQUESTS_LIST_PARAMETERS } from '@entities/request/config/store-constants';
import { ROUTE_ADMIN_PRODUCT_CREATE_ORDER_BASE } from '@shared/config/routes/routes';
import { useRedirect } from '@shared/lib/hooks/use-redirect/use-redirect';

export const usePurchaseEmployee = ({
    product,
    productCounter,
}: {
    product: ProductListItem;
    productCounter: number;
}) => {
    const redirect = useRedirect();
    const user = useCurrentUserSelector();
    const [, , resetRequestsList] = useRequestsListSharedQuery(DEFAULT_REQUESTS_LIST_PARAMETERS);
    const [createRequest, createRequestResult] = useCreateRequestMutation({
        fixedCacheKey: CACHE_KEY_EMPLOYEE_CREATE_REQUEST,
    });

    const handleCreateOrderEmployee = async () => {
        redirect({ url: `${ROUTE_ADMIN_PRODUCT_CREATE_ORDER_BASE}/${product.id}?count=${productCounter}` });
    };

    const handleRequestEmployee = async () => {
        const role = user?.role.code;

        if (!role) {
            return;
        }
        await createRequest({ productId: product.id, quantity: productCounter, userId: user.id });
        resetRequestsList();
    };

    useEffect(() => {
        if (createRequestResult.isSuccess) {
            toast.custom((t) => {
                return (
                    <Toast
                        type='success'
                        title='Успешно!'
                        description={'Запрос успешно создан'}
                        onClose={() => {
                            toast.remove(t.id);
                        }}
                    />
                );
            });
            createRequestResult.reset();
        } else if (createRequestResult.isError) {
            toast.custom((t) => {
                return (
                    <Toast
                        type='error'
                        title='Ошибка!'
                        description={`Не удалось создать запрос.\n${getQueryError(createRequestResult)?.message}`}
                        onClose={() => {
                            toast.remove(t.id);
                        }}
                    />
                );
            });
            createRequestResult.reset();
        }
    }, [createRequestResult.isSuccess, createRequestResult.isError]);

    return {
        handleCreateOrderEmployee,
        handleRequestEmployee,
    };
};
